import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { forwardRef, memo, useMemo, } from 'react';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { Icon, } from '../Icon';
import { Tooltip } from '../Tooltip';
import { colorStyles, fillColorStyles, } from './styles';
const handleSize = styleUtils.rem(20);
const sliderIconHandlerWidth = 24;
const sliderIconHandlerHeight = 24;
const sliderIconHandleBoxShadowHorizontalOffset = 0.5;
const sliderIconBoxShadowVerticalOffset = 2.25;
const sliderHandleInteractiveStyles = css `
  padding: 0;
`;
const sliderHandleBorderStyles = css `
  align-items: center;
  border-radius: 50%;
  display: flex;
  height: 100%;
  justify-content: center;
  padding: ${styleUtils.rem(2)};
  width: 100%;
`;
const sliderHandleBorderInteractiveStyles = css `
  background-color: ${theme.colors.gray400};
  box-shadow: 0 0 ${styleUtils.rem(3)} 0 ${theme.colors.gray400};
`;
const sliderHandleInnerStyles = css `
  ${styleUtils.transition('background-color')}
  border-radius: 50%;
  height: 100%;
  width: 100%;
`;
const sliderHandleInnerInteractiveStyles = css `
  background-color: ${theme.colors.gray100};
`;
const sliderHandleTooltipStyles = css `
  user-select: none;
`;
const sliderHandleTooltipContentStyles = css `
  line-height: 1;
  user-select: none;
`;
const defaultProps = {
    tooltipContent: null,
};
// eslint-disable-next-line react/display-name
const SliderHandle = forwardRef(({ ariaDescribedBy, ariaLabel, ariaLabelledBy, color, hasIndicatorCssAnimation, hasTooltip, isDragging, isHovering, isUserInteractable, relativePosition, tooltipContent = defaultProps.tooltipContent, handleIconType, onMoveRight, onMoveLeft, }, ref) => {
    const sliderHandleStyles = useMemo(() => (css `
          border-radius: 50%;
          height: ${handleSize};
          outline: 0;
          padding: ${styleUtils.rem(2)};
          position: absolute;
          width: ${handleSize};

          ${handleIconType && css `
            align-items: center;
            background-color: ${theme.colors.gray100};
            box-shadow: 0 ${styleUtils.rem(sliderIconHandleBoxShadowHorizontalOffset)} ${styleUtils.rem(sliderIconBoxShadowVerticalOffset)} 0 ${theme.colors.gray800};
            display: flex;
            height: ${styleUtils.rem(sliderIconHandlerHeight)};
            justify-content: center;
            width: ${styleUtils.rem(sliderIconHandlerWidth)};
          `}

          &:focus-visible {
            ${styleUtils.focusVisibleStyles};
          }
        `), [
        handleIconType,
    ]);
    return (_jsxs("div", Object.assign({ "aria-describedby": ariaDescribedBy, "aria-label": `${ariaLabel} handle`, "aria-labelledby": ariaLabelledBy, css: css `
          ${sliderHandleStyles}
          ${!handleIconType && isUserInteractable &&
            (isDragging || isHovering) &&
            sliderHandleInteractiveStyles}
        ${hasIndicatorCssAnimation
            ? styleUtils.transition([
                'left',
                'padding',
            ])
            : styleUtils.transition('padding')}
        will-change: left, padding, transition;
        `, onKeyDown: 
        //Here we want to handle the keyboard events, in this case we want to handle the arrow keys
        (event) => {
            if (event.key === 'ArrowRight') {
                onMoveRight();
            }
            if (event.key === 'ArrowLeft') {
                onMoveLeft();
            }
        }, ref: ref, role: "button", 
        // We want to use the `style` prop for any dynamic values
        // so we don't force emotion to recompute a new node
        // each time the value is changed.
        //
        // https://emotion.sh/docs/best-practices#use-the-style-prop-for-dynamic-styles
        style: {
            left: `calc(${relativePosition * 100}% - (${handleSize} / 2))`,
        }, tabIndex: 0 }, { children: [hasTooltip && (_jsx("div", Object.assign({ css: sliderHandleTooltipStyles }, { children: _jsx(Tooltip, Object.assign({ alignContent: "top", enterDelay: true, exitDelay: true, minWidth: false, noPadding: true, placement: "top", showTooltip: isDragging || isHovering }, { children: _jsx("div", Object.assign({ css: sliderHandleTooltipContentStyles }, { children: tooltipContent })) })) }))), handleIconType
                ? (_jsx(Icon, { size: "extraSmall", type: handleIconType }))
                : (_jsx("div", Object.assign({ css: css `
            ${sliderHandleBorderStyles}
            ${colorStyles[color]}
          ${isUserInteractable &&
                        isDragging &&
                        sliderHandleBorderInteractiveStyles}
          will-change: background-color, box-shadow;
          ` }, { children: _jsx("div", { css: css `
              ${sliderHandleInnerStyles}
              ${fillColorStyles[color]}
            ${isUserInteractable &&
                            (isDragging || isHovering) &&
                            sliderHandleInnerInteractiveStyles}
            will-change: background-color;
            ` }) })))] })));
});
const MemoizedSliderHandle = memo(SliderHandle);
export { MemoizedSliderHandle as SliderHandle };
// MC-1068 Temporary hack for Storybook.
export default MemoizedSliderHandle;
